import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import {
    Button,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    useBoolean
} from '@chakra-ui/react';
import { FaReply } from 'react-icons/fa6';
import { FiCornerDownLeft, FiCornerUpRight, FiExternalLink, FiFile, FiMail } from 'react-icons/fi';

import { ActivityFile, ActivityParticipantMessage } from 'data/link/schemas';
import { EmailContent } from 'models/link/Task';

import { ReplyEmailSupport } from 'models/user/UserSupport';
import { QuillEditor } from 'common/components';
import { extractAccessToken, formatCompactDateTimeString } from 'common/helpers';
import { TicketListItem } from 'models/link/Link';

const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp']);
const VIDEO_EXTENSIONS = new Set(['mp4', 'webm', 'ogg', 'mov', 'm4v', 'mkv']);

function getSafeAttachmentUrl(value?: string | null) {
    if (!value) return undefined;

    try {
        const url = new URL(value);
        return url.protocol === 'https:' ? url.toString() : undefined;
    } catch {
        return undefined;
    }
}

function getSafeImageUrl(value?: string | null) {
    if (!value) return undefined;

    try {
        const url = new URL(value);
        return url.protocol === 'https:' ? url.toString() : undefined;
    } catch {
        return undefined;
    }
}

function getInitials(name?: string | null) {
    if (!name?.trim()) return '?';

    return name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map(part => part.charAt(0))
        .join('')
        .toUpperCase();
}

function getMessageTime(value?: string | null) {
    const formattedDateTime = formatCompactDateTimeString(value);
    if (!formattedDateTime) return '';

    const [, ...timeParts] = formattedDateTime.split(/\s+/);
    return timeParts.join(' ') || formattedDateTime;
}

function renderQuotedText(value?: string | null) {
    return (value || '-')
        .split(/("[^"]*")/g)
        .filter(Boolean)
        .map((part, index) =>
            part.startsWith('"') && part.endsWith('"') ? (
                <strong
                    key={`${part}-${index}`}
                    className="font-semibold text-[#1C1E24]"
                >
                    {part}
                </strong>
            ) : (
                <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
            )
        );
}

function MessageAvatar({
    name,
    image,
    large = false
}: {
    name?: string | null;
    image?: string | null;
    large?: boolean;
}) {
    const imageUrl = getSafeImageUrl(image);
    const [hasImageError, setHasImageError] = useState(false);
    const sizeClassName = large ? 'h-[34px] w-[34px] text-xs' : 'h-[30px] w-[30px] text-[10px]';

    if (imageUrl && !hasImageError) {
        return (
            <img
                src={imageUrl}
                alt={name || ''}
                loading="lazy"
                referrerPolicy="no-referrer"
                onError={() => setHasImageError(true)}
                className={`${sizeClassName} shrink-0 rounded-full object-cover`}
            />
        );
    }

    return (
        <span
            className={`flex ${sizeClassName} shrink-0 items-center justify-center rounded-full bg-[#EFF4FF] font-semibold text-[#1D4ED8]`}
        >
            {getInitials(name)}
        </span>
    );
}

function getFileExtension(file: ActivityFile) {
    const value = file.fileName || file.url;

    try {
        const path = file.fileName || new URL(file.url).pathname;
        return path.split('.').pop()?.toLowerCase() ?? '';
    } catch {
        return value.split('?')[0].split('#')[0].split('.').pop()?.toLowerCase() ?? '';
    }
}

function getAttachmentKind(file: ActivityFile) {
    const type = file.type?.trim().toUpperCase();
    const extension = getFileExtension(file);

    if (type === 'IMAGE' || IMAGE_EXTENSIONS.has(extension)) return 'image';
    if (type === 'VIDEO' || VIDEO_EXTENSIONS.has(extension)) return 'video';
    return 'file';
}

function AttachmentLink({ file, url }: { file: ActivityFile; url?: string }) {
    const fileName = file.fileName || 'ไฟล์แนบ';

    if (!url) {
        return (
            <div className="flex min-w-0 items-center gap-2 rounded-[6px] border border-[#E1E4E8] bg-[#F8F9FA] px-3 py-2 text-sm text-[#5A6070]">
                <FiFile className="shrink-0" />
                <span
                    className="truncate"
                    title={fileName}
                >
                    {fileName}
                </span>
            </div>
        );
    }

    return (
        <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            referrerPolicy="no-referrer"
            className="flex min-w-0 items-center gap-2 rounded-[6px] border border-[#E1E4E8] bg-[#F8F9FA] px-3 py-2 text-sm text-[#1D4ED8] transition hover:border-[#2563EB] hover:bg-[#EFF4FF]"
        >
            <FiFile className="shrink-0" />
            <span
                className="truncate"
                title={fileName}
            >
                {fileName}
            </span>
            <FiExternalLink className="ml-auto shrink-0" />
        </a>
    );
}

function RemarkAttachment({ file }: { file: ActivityFile }) {
    const url = getSafeAttachmentUrl(file.url);
    const kind = getAttachmentKind(file);
    const [hasMediaError, setHasMediaError] = useState(false);
    const fileName = file.fileName || 'ไฟล์แนบ';

    if (!url || kind === 'file' || hasMediaError) {
        return (
            <AttachmentLink
                file={file}
                url={url}
            />
        );
    }

    if (kind === 'image') {
        return (
            <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                referrerPolicy="no-referrer"
                className="block overflow-hidden rounded-[6px] border border-[#E1E4E8] bg-[#F8F9FA]"
            >
                <img
                    src={url}
                    alt={fileName}
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    onError={() => setHasMediaError(true)}
                    className="max-h-64 w-full object-contain"
                />
                <p className="truncate border-t border-[#E1E4E8] px-3 py-2 text-xs text-[#5A6070]">
                    {fileName}
                </p>
            </a>
        );
    }

    return (
        <div className="overflow-hidden rounded-[6px] border border-[#E1E4E8] bg-[#F8F9FA]">
            <video
                controls
                preload="metadata"
                onError={() => setHasMediaError(true)}
                className="max-h-72 w-full bg-black"
            >
                <source src={url} />
            </video>
            <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                referrerPolicy="no-referrer"
                className="flex min-w-0 items-center gap-2 border-t border-[#E1E4E8] px-3 py-2 text-xs text-[#1D4ED8] hover:bg-[#EFF4FF]"
            >
                <span
                    className="truncate"
                    title={fileName}
                >
                    {fileName}
                </span>
                <FiExternalLink className="ml-auto shrink-0" />
            </a>
        </div>
    );
}

type Props = {
    noReplyButton?: boolean;
    task: TicketListItem;
    activity: ActivityParticipantMessage;
    onReplyEmail: (reply: ReplyEmailSupport) => void;
};

function ParticipantMessage(props: Props) {
    const { noReplyButton, task, activity, onReplyEmail } = props;

    const [showReplyModal, setShowReplyModal] = useBoolean();
    const [isEmailExpanded, setIsEmailExpanded] = useState(false);
    const messageType = activity.messageType?.trim().toUpperCase() ?? '';
    const isEmailMessage = messageType.includes('EMAIL');
    const isIncomingEmail = messageType === 'EMAIL_INCOMING';
    const isOutgoingEmail = messageType === 'EMAIL_OUTGOING';
    const isRemarkMessage = messageType === 'REMARK';

    const email = useMemo(() => {
        if (!isEmailMessage || !activity.data) {
            return {} as EmailContent;
        }

        try {
            return JSON.parse(activity.data) as EmailContent;
        } catch {
            return {} as EmailContent;
        }
    }, [activity.data, isEmailMessage]);
    const messageContent = isEmailMessage
        ? email.content_body || activity.messageText
        : activity.messageText ||
          activity.remarkEventDetail ||
          activity.eventMessage ||
          activity.data;
    const safeMessageContent = useMemo(
        () =>
            DOMPurify.sanitize(
                messageContent || '-',
                isEmailMessage ? { FORBID_TAGS: ['img'] } : undefined
            ),
        [isEmailMessage, messageContent]
    );
    const quotedEmailContent = useMemo(
        () =>
            DOMPurify.sanitize(
                '<p><br /></p>' +
                    '<p>______________________________</p>' +
                    '<p><b>From: </b>' +
                    (email.email_from ?? '') +
                    '</p>' +
                    '<p><b>Subject: </b>' +
                    (email.subject ?? '') +
                    '</p>' +
                    '<p><br /></p>' +
                    safeMessageContent
            ),
        [email.email_from, email.subject, safeMessageContent]
    );

    const emailFrom = useRef(extractAccessToken().email).current;
    const [emailContent, setEmailContent] = useState(() => quotedEmailContent);

    const handleClickReply = useCallback(async () => {
        onReplyEmail({
            account: {
                sid: email.system_id || '',
                companycode: '1000',
                email: emailFrom
            },
            reply_data: {
                // content_body: emailContent.replace(/<[^>]+>/g, '\n'), // Replace all html tags with new line
                content_body: emailContent,
                email_from: emailFrom,
                mail_to_list: [email.email_from],
                // mail_cc_list: Array.from(new Set([...email.mail_cc_list, emailCC])),
                mail_cc_list: [''],
                object_link: email.object_link,
                ref_email_code: email.ref_email_code,
                subject: email.subject,
                system_id: email.system_id,
                type: ''
            }
        });
    }, [emailFrom, emailContent, email, onReplyEmail]);

    const canReply =
        !noReplyButton &&
        isEmailMessage &&
        Boolean(email.email_from && email.object_link && email.ref_email_code);
    const createdOn = getMessageTime(activity.createdOn);
    const emailSender = activity.creatorFullname || email.email_from || '-';
    const emailRecipients = (email.mail_to_list ?? []).filter(Boolean).join(', ');
    const emailCc = (email.mail_cc_list ?? []).filter(Boolean).join(', ');
    const emailSubject = email.subject || activity.messageText || '-';
    const messageClassName = isRemarkMessage
        ? 'w-full px-0.5'
        : isEmailMessage
        ? `w-full overflow-hidden rounded-[6px] border border-l-4 bg-white ${
              isIncomingEmail
                  ? 'border-[#E1E4E8] border-l-[#16803C]'
                  : isOutgoingEmail
                  ? 'border-[#E1E4E8] border-l-[#DC2626]'
                  : 'border-[#E1E4E8]'
          }`
        : 'w-full px-0.5';

    return (
        <React.Fragment>
            <div className="flex">
                <div className={messageClassName}>
                    <div className={isRemarkMessage ? 'py-2' : isEmailMessage ? 'p-3.5' : 'py-2.5'}>
                        {isEmailMessage ? (
                            <div className="flex items-start gap-3">
                                {isIncomingEmail ? (
                                    <FiCornerDownLeft className="mt-1.5 shrink-0 text-lg text-[#16803C]" />
                                ) : (
                                    <FiCornerUpRight className="mt-1.5 shrink-0 text-lg text-[#DC2626]" />
                                )}
                                <MessageAvatar
                                    name={emailSender}
                                    image={activity.image}
                                    large
                                />
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-start gap-3">
                                        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-[#1C1E24]">
                                            {isIncomingEmail && 'Receiver: '}
                                            {emailSender}
                                        </span>
                                        {createdOn && (
                                            <span className="shrink-0 text-xs text-[#9099A8]">
                                                {createdOn}
                                            </span>
                                        )}
                                    </div>
                                    <div className="mt-1 flex items-center gap-3">
                                        <div className="min-w-0 flex-1">
                                            <p className="truncate text-xs leading-5 text-[#5A6070]">
                                                From {email.email_from || '-'}
                                                {emailRecipients && `  ·  To ${emailRecipients}`}
                                            </p>
                                            {emailCc && (
                                                <p className="truncate text-xs leading-5 text-[#5A6070]">
                                                    CC {emailCc}
                                                </p>
                                            )}
                                            <p
                                                title={emailSubject}
                                                className="truncate text-xs leading-5 text-[#5A6070]"
                                            >
                                                {emailSubject}
                                            </p>
                                        </div>
                                        {canReply && isEmailExpanded && (
                                            <Button
                                                size="sm"
                                                onClick={setShowReplyModal.on}
                                                className="flex shrink-0 items-center gap-2 rounded-[6px] bg-[#2563EB] text-white transition hover:bg-[#1D4ED8]"
                                            >
                                                <FaReply />
                                                <span>Reply</span>
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-start gap-2">
                                <MessageAvatar
                                    name={activity.creatorFullname}
                                    image={activity.image}
                                />
                                <div className="min-w-0 flex-1">
                                    {isRemarkMessage ? (
                                        <React.Fragment>
                                            <div className="mb-1.5 flex min-w-0 flex-wrap items-start gap-x-2 gap-y-1 text-xs text-[#5A6070]">
                                                {activity.creatorFullname && (
                                                    <span className="font-semibold text-[#1C1E24]">
                                                        {activity.creatorFullname}
                                                    </span>
                                                )}
                                                {messageType && (
                                                    <span className="rounded-[3px] border border-[#FCD34D] bg-[#FEF3C7] px-2 py-0.5 text-[10px] font-medium text-[#B45309]">
                                                        {messageType}
                                                    </span>
                                                )}
                                                {createdOn && (
                                                    <span className="ml-auto shrink-0">
                                                        {createdOn}
                                                    </span>
                                                )}
                                            </div>
                                            <div
                                                className="whitespace-pre-wrap break-words text-sm leading-relaxed text-[#1C1E24]"
                                                dangerouslySetInnerHTML={{
                                                    __html: safeMessageContent
                                                }}
                                            />
                                        </React.Fragment>
                                    ) : (
                                        <React.Fragment>
                                            {(activity.creatorFullname ||
                                                createdOn ||
                                                messageType) && (
                                                <div className="mb-1 flex min-w-0 flex-wrap items-start gap-x-2 gap-y-1 text-xs text-[#5A6070]">
                                                    {activity.creatorFullname && (
                                                        <span className="font-semibold text-[#1C1E24]">
                                                            {activity.creatorFullname}
                                                        </span>
                                                    )}
                                                    {messageType && <span>{messageType}</span>}
                                                    {createdOn && (
                                                        <span className="ml-auto">{createdOn}</span>
                                                    )}
                                                </div>
                                            )}
                                            {messageType ? (
                                                <div
                                                    className="whitespace-pre-wrap break-words text-sm leading-relaxed text-[#1C1E24]"
                                                    dangerouslySetInnerHTML={{
                                                        __html: safeMessageContent
                                                    }}
                                                />
                                            ) : (
                                                <div className="whitespace-pre-wrap break-words text-sm leading-relaxed text-[#5A6070]">
                                                    {renderQuotedText(messageContent)}
                                                </div>
                                            )}
                                        </React.Fragment>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {isEmailMessage && (
                        <React.Fragment>
                            <button
                                type="button"
                                onClick={() => setIsEmailExpanded(value => !value)}
                                className="flex w-full items-center gap-2 border-t border-[#E1E4E8] px-3.5 py-2 text-left text-xs font-medium text-[#2563EB] transition hover:bg-[#EFF4FF]"
                            >
                                <FiMail className="shrink-0" />
                                {isEmailExpanded ? 'ซ่อนเนื้อหาอีเมล' : 'แสดงเนื้อหาอีเมล'}
                            </button>
                            {isEmailExpanded && (
                                <div
                                    className="break-words border-t border-[#E1E4E8] bg-white px-4 py-3 text-sm leading-relaxed text-[#1C1E24] [&_a]:text-[#1D4ED8] [&_a]:underline"
                                    dangerouslySetInnerHTML={{
                                        __html: safeMessageContent
                                    }}
                                />
                            )}
                        </React.Fragment>
                    )}

                    {activity.files && activity.files.length > 0 && (
                        <div className="border-t border-[#E1E4E8] px-3.5 py-2.5">
                            <p className="mb-2 text-xs font-medium text-[#5A6070]">ไฟล์แนบ</p>
                            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                {activity.files.map((file, index) => (
                                    <RemarkAttachment
                                        key={`${file.key || index}-${file.url}`}
                                        file={file}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <Modal
                size="6xl"
                isOpen={showReplyModal}
                onClose={setShowReplyModal.off}
            >
                <ModalOverlay />
                <ModalContent className="ticket-detail-v2">
                    <ModalHeader className="text-[22px]">
                        Reply <span className="text-[#2563EB]">{task.ticketNO}</span>
                    </ModalHeader>
                    <ModalBody className="space-y-4">
                        {/* <div>
                            <Label>E-mail from</Label>
                            <Input
                                disabled
                                placeholder="E-mail from"
                                value={emailFrom}
                            />
                        </div>

                        <div>
                            <Label>E-mail to</Label>
                            <Input
                                disabled
                                placeholder="E-mail to"
                                value={email.email_from}
                            />
                        </div> */}

                        {/* <div>
                            <Label>E-mail CC</Label>
                            <Input
                                placeholder="E-mail CC"
                                value={emailCC}
                                onChange={event => setEmailCC(event.target.value)}
                            />
                        </div> */}

                        <QuillEditor
                            value={emailContent}
                            onChange={setEmailContent}
                            editorClassName="[&>.ql-container]:min-h-[250px]"
                        />
                    </ModalBody>
                    <ModalFooter className="gap-2">
                        <Button
                            size="sm"
                            variant="outline"
                            className="rounded-[6px] border-[#C9CDD4] bg-white text-sm leading-none text-[#5A6070] transition hover:bg-[#F8F9FA]"
                            onClick={setShowReplyModal.off}
                        >
                            Cancel
                        </Button>
                        <Button
                            size="sm"
                            onClick={handleClickReply}
                            className="rounded-[6px] bg-[#2563EB] text-sm leading-none text-white transition hover:bg-[#1D4ED8]"
                        >
                            Send
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </React.Fragment>
    );
}

export default memo(ParticipantMessage);
